// src/pages/courierInvoice/CreateCourierInvoicePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  Eye,
  Download,
  Pause,
  Play,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  FileSpreadsheet,
  Truck,
  ArrowLeft
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  getBusinessCouriers
} from '../../services/api/businessCourier';
import {
  getColumnMappingForCourier,
  initializeInvoice,
  uploadChunk,
  finalizeInvoice,
  validateChunk,
  cancelInvoice,
  getEligibleOrdersCount
} from '../../services/api/courierInvoice';
import {
  PageHeader,
} from '../../components/common';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import DatePicker from '../../components/ui/DatePicker/DatePicker';
import styles from './CreateCourierInvoicePage.module.css';

const CreateCourierInvoicePage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const fileInputRef = useRef(null);

  // State management
  const [businessCouriers, setBusinessCouriers] = useState([]);
  const [selectedCourierId, setSelectedCourierId] = useState('');
  const [columnMapping, setColumnMapping] = useState(null);
  const [eligibleOrdersInfo, setEligibleOrdersInfo] = useState(null);

  // Invoice form state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [filePreview, setFilePreview] = useState([]);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  // Processing state
  const [uploadState, setUploadState] = useState('idle');
  const [invoiceId, setInvoiceId] = useState(null);
  const [uploadToken, setUploadToken] = useState(null);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [chunkSize] = useState(200);

  // Progress tracking
  const [progressStats, setProgressStats] = useState({
    processedCount: 0,
    matched: 0,
    notFound: 0,
    alreadyInvoiced: 0,
    ineligible: 0,
    errors: []
  });

  // Final results
  const [finalResults, setFinalResults] = useState(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);
  const [mappingWarnings, setMappingWarnings] = useState([]);

  // Utility function to parse currency values
  const parseCurrencyValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    // Convert to string if not already
    let strValue = value.toString().trim();

    // Remove common currency symbols and text
    // Handles: LKR, Rs, Rs., USD, $, £, €, /-
    strValue = strValue.replace(/^(LKR|USD|INR|Rs\.?|₹|\$|£|€)\s*/i, '');
    strValue = strValue.replace(/\/-$/, ''); // Remove trailing /-

    // Remove all commas (thousands separators)
    strValue = strValue.replace(/,/g, '');

    // Remove any remaining whitespace
    strValue = strValue.trim();

    // Parse to float
    const numValue = parseFloat(strValue);

    // Return 0 if parsing failed or resulted in NaN
    return isNaN(numValue) ? 0 : numValue;
  };

  // Load business couriers on mount
  useEffect(() => {
    loadBusinessCouriers();
  }, []);

  // Load column mapping when courier is selected
  useEffect(() => {
    if (selectedCourierId) {
      loadColumnMapping();
      loadEligibleOrders();
    } else {
      setColumnMapping(null);
      setEligibleOrdersInfo(null);
    }
  }, [selectedCourierId]);

  const loadBusinessCouriers = async () => {
    try {
      const response = await getBusinessCouriers();
      setBusinessCouriers(response.couriers || []);
    } catch (error) {
      console.error('Failed to load business couriers:', error);
      showError(t('courierInvoice.failedToLoadCouriers'));
    }
  };

  const loadColumnMapping = async () => {
    try {
      const courier = businessCouriers.find(c => c.id.toString() === selectedCourierId);
      if (!courier) return;

      const response = await getColumnMappingForCourier(courier.id);
      setColumnMapping(response.mapping);
      console.log('Loaded column mapping:', response.mapping);
    } catch (error) {
      console.error('Failed to load column mapping:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToLoadMapping'));
      setColumnMapping(null);
    }
  };

  const loadEligibleOrders = async () => {
    try {
      const response = await getEligibleOrdersCount(selectedCourierId);
      setEligibleOrdersInfo(response);
    } catch (error) {
      console.error('Failed to load eligible orders:', error);
      setEligibleOrdersInfo(null);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(file.type)) {
      showError(t('courierInvoice.invalidFileType'));
      return;
    }

    setSelectedFile(file);
    setUploadState('validating');

    try {
      // Read file
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, {
        cellDates: true,
        cellNF: true,
        cellStyles: true
      });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // Convert to JSON - get all rows first
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        raw: false,
        dateNF: 'yyyy-mm-dd',
        defval: '' // Set default value for empty cells
      });

      if (jsonData.length < 2) {
        showError(t('courierInvoice.fileMustHaveData'));
        resetFileUpload();
        return;
      }

      // Get header row index and data start index from mapping config
      const headerRowIndex = columnMapping?.mappingConfig?.headerRowIndex || 1;
      const dataStartRowIndex = columnMapping?.mappingConfig?.dataStartRowIndex || 2;
      const skipRows = columnMapping?.mappingConfig?.skipRows || [];

      // Get headers from the specified row (convert to 0-based index)
      const headers = jsonData[headerRowIndex - 1];
      
      // Get data rows starting from dataStartRowIndex
      const allDataRows = jsonData.slice(dataStartRowIndex - 1);
      
      // Filter out empty rows and skip rows
      const dataRows = allDataRows.filter((row, index) => {
        const actualRowNumber = dataStartRowIndex + index;
        
        // Skip if in skip list
        if (skipRows.includes(actualRowNumber - 1)) {
          return false;
        }
        
        // Skip if all cells are empty
        return row.some(cell => cell !== null && cell !== undefined && cell !== '');
      });

      setFileHeaders(headers);
      setFileData(dataRows);
      setTotalRows(dataRows.length);
      setFilePreview(dataRows.slice(0, 5));

      // Validate against column mapping if available
      if (columnMapping) {
        validateFileStructure(headers);
      }

      setUploadState('ready');
    } catch (error) {
      console.error('Failed to read file:', error);
      showError(t('courierInvoice.failedToReadFile'));
      resetFileUpload();
    }
  };

  const validateFileStructure = (headers) => {
    if (!columnMapping?.mappingConfig?.columnMappings) {
      return;
    }

    const warnings = [];
    const requiredColumns = columnMapping.mappingConfig.columnMappings;
    
    // Check if all required columns exist
    Object.keys(requiredColumns).forEach(fieldName => {
      const mapping = requiredColumns[fieldName];
      
      // Skip formula-based fields as they don't need source columns
      if (mapping.type === 'formula') {
        return;
      }
      
      const columnName = mapping.columnName;
      const isRequired = mapping.required !== false; // Default to true if not specified
      
      if (isRequired && !headers.includes(columnName)) {
        warnings.push({
          type: 'missing_column',
          field: fieldName,
          expectedColumn: columnName,
          message: t('courierInvoice.missingRequiredColumn', { 
            column: columnName, 
            field: fieldName 
          })
        });
      }
    });

    setMappingWarnings(warnings);
  };

  const resetFileUpload = () => {
    setSelectedFile(null);
    setFileData(null);
    setFilePreview([]);
    setFileHeaders([]);
    setTotalRows(0);
    setUploadState('idle');
    setValidationErrors([]);
    setMappingWarnings([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const transformFileDataToOrders = () => {
    if (!fileData || !columnMapping?.mappingConfig?.columnMappings) {
      return [];
    }

    const mappings = columnMapping.mappingConfig.columnMappings;
    const orders = [];
    const headerRowIndex = columnMapping.mappingConfig.headerRowIndex || 1;
    const dataStartRowIndex = columnMapping.mappingConfig.dataStartRowIndex || 2;

    fileData.forEach((row, index) => {
      const order = { 
        rowNumber: dataStartRowIndex + index // Actual row number in Excel
      };

      // First pass: Map direct column values
      Object.keys(mappings).forEach(fieldName => {
        const mapping = mappings[fieldName];
        
        // Skip formula fields in first pass
        if (mapping.type === 'formula') {
          return;
        }
        
        const columnIndex = fileHeaders.indexOf(mapping.columnName);
        
        if (columnIndex !== -1) {
          let value = row[columnIndex];
          
          // Handle empty values
          if (value === null || value === undefined || value === '') {
            value = mapping.defaultValue !== undefined ? mapping.defaultValue : null;
          }
          
          // Apply type conversions
          if (value !== null) {
            switch (mapping.type) {
              case 'number':
                // Special handling for currency fields (codAmount and courierCharge)
                if (fieldName === 'codAmount' || fieldName === 'courierCharge' || fieldName === 'paidAmount') {
                  value = parseCurrencyValue(value);
                } else {
                  value = parseFloat(value) || (mapping.defaultValue !== undefined ? mapping.defaultValue : 0);
                }
                break;
              case 'string':
                value = value.toString().trim();
                break;
              case 'enum':
                // Apply value transformations for enum types
                value = value.toString().trim();
                if (mapping.valueTransformations && mapping.valueTransformations[value]) {
                  value = mapping.valueTransformations[value];
                } else if (mapping.defaultValue !== undefined) {
                  value = mapping.defaultValue;
                }
                break;
              default:
                value = value.toString().trim();
            }
          }
          
          order[fieldName] = value;
        } else if (mapping.defaultValue !== undefined) {
          order[fieldName] = mapping.defaultValue;
        }
      });

      // Second pass: Calculate formula-based fields
      Object.keys(mappings).forEach(fieldName => {
        const mapping = mappings[fieldName];
        
        if (mapping.type === 'formula' && mapping.formula) {
          const formula = mapping.formula;
          
          switch (formula.operation) {
            case 'sum':
              // Sum multiple columns
              let sum = 0;
              formula.columns.forEach(colName => {
                const colIndex = fileHeaders.indexOf(colName);
                if (colIndex !== -1) {
                  const val = parseCurrencyValue(row[colIndex]);
                  sum += val;
                }
              });
              order[fieldName] = sum;
              break;
              
            case 'subtract':
              // Subtract two values
              const minuend = order[formula.minuend] || 0;
              const subtrahend = order[formula.subtrahend] || 0;
              order[fieldName] = minuend - subtrahend;
              break;
              
            default:
              order[fieldName] = mapping.defaultValue || 0;
          }
        }
      });

      // Calculate amountPayable (COD - courierCharge)
      // Ensure both values are properly parsed as currency
      if (order.codAmount !== undefined && order.courierCharge !== undefined) {
        const codAmount = typeof order.codAmount === 'number' 
          ? order.codAmount 
          : parseCurrencyValue(order.codAmount);
        const courierCharge = typeof order.courierCharge === 'number' 
          ? order.courierCharge 
          : parseCurrencyValue(order.courierCharge);
        
        order.amountPayable = codAmount - courierCharge;
      }

      orders.push(order);
    });

    console.log('Transformed orders sample:', orders.slice(0, 2));
    return orders;
  };

  const handleStartUpload = async () => {
    // Validate inputs
    if (!selectedCourierId) {
      showError(t('courierInvoice.selectCourierFirst'));
      return;
    }

    if (!invoiceNumber.trim()) {
      showError(t('courierInvoice.enterInvoiceNumber'));
      return;
    }

    if (!invoiceDate) {
      showError(t('courierInvoice.selectInvoiceDate'));
      return;
    }

    if (!selectedFile || !fileData) {
      showError(t('courierInvoice.uploadFileFirst'));
      return;
    }

    if (mappingWarnings.length > 0) {
      const confirmed = window.confirm(
        t('courierInvoice.mappingWarningsConfirm')
      );
      if (!confirmed) return;
    }

    try {
      setUploadState('uploading');

      // Initialize invoice
      const initResponse = await initializeInvoice({
        invoiceNumber: invoiceNumber.trim(),
        businessCourierId: parseInt(selectedCourierId),
        invoiceDate,
        totalOrders: totalRows,
        metadata: {
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          totalRowsInFile: totalRows
        }
      });

      setInvoiceId(initResponse.invoiceId);
      setUploadToken(initResponse.uploadToken);

      // Transform data to orders
      const orders = transformFileDataToOrders();
      console.log('Total orders to upload:', orders.length);
      console.log('Sample order:', orders[0]);
      
      const chunks = [];
      const calculatedChunkSize = chunkSize || initResponse.chunkSize;

      for (let i = 0; i < orders.length; i += calculatedChunkSize) {
        chunks.push(orders.slice(i, i + calculatedChunkSize));
      }

      setTotalChunks(chunks.length);

      // Start uploading chunks
      await uploadChunks(initResponse.invoiceId, initResponse.uploadToken, chunks);

    } catch (error) {
      console.error('Failed to start upload:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToStartUpload'));
      setUploadState('error');
    }
  };

  const uploadChunks = async (invId, token, chunks) => {
    const stats = {
      processedCount: 0,
      matched: 0,
      notFound: 0,
      alreadyInvoiced: 0,
      ineligible: 0,
      errors: []
    };

    for (let i = 0; i < chunks.length; i++) {
      if (uploadState === 'paused') {
        // Wait until resumed
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (uploadState === 'uploading') {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      }

      setCurrentChunk(i + 1);

      try {
        console.log(`Uploading chunk ${i + 1}/${chunks.length} with ${chunks[i].length} orders`);
        
        const chunkResult = await uploadChunk(invId, {
          uploadToken: token,
          chunkIndex: i,
          totalChunks: chunks.length,
          orders: chunks[i]
        });

        console.log(`Chunk ${i + 1} result:`, chunkResult);

        // Aggregate stats
        stats.processedCount += chunkResult.processedCount || 0;
        stats.matched += chunkResult.matched || 0;
        stats.notFound += chunkResult.notFound || 0;
        stats.alreadyInvoiced += chunkResult.alreadyInvoiced || 0;
        stats.ineligible += chunkResult.ineligible || 0;
        stats.errors.push(...(chunkResult.errors || []));

        setProgressStats({ ...stats });

      } catch (error) {
        console.error(`Failed to upload chunk ${i + 1}:`, error);
        showError(t('courierInvoice.failedToUploadChunk'));
        setUploadState('error');
        throw error;
      }
    }

    // Finalize invoice
    try {
      console.log('Finalizing invoice...');
      
      const finalResult = await finalizeInvoice(invId, {
        uploadToken: token,
        totalChunksSent: chunks.length
      });

      console.log('Final result:', finalResult);

      setFinalResults(finalResult);
      setUploadState('completed');
      success(t('courierInvoice.invoiceUploadedSuccessfully'));

    } catch (error) {
      console.error('Failed to finalize invoice:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToFinalizeInvoice'));
      setUploadState('error');
    }
  };

  const handlePauseResume = () => {
    if (uploadState === 'uploading') {
      setUploadState('paused');
    } else if (uploadState === 'paused') {
      setUploadState('uploading');
    }
  };

  const handleCancelUpload = async () => {
    const confirmed = window.confirm(
      t('courierInvoice.cancelUploadConfirm')
    );
    
    if (!confirmed) return;

    try {
      if (invoiceId) {
        await cancelInvoice(invoiceId);
      }
      resetUpload();
      success(t('courierInvoice.invoiceUploadCancelled'));
    } catch (error) {
      console.error('Failed to cancel invoice:', error);
      showError(t('courierInvoice.failedToCancelInvoice'));
    }
  };

  const resetUpload = () => {
    setInvoiceId(null);
    setUploadToken(null);
    setCurrentChunk(0);
    setTotalChunks(0);
    setProgressStats({
      processedCount: 0,
      matched: 0,
      notFound: 0,
      alreadyInvoiced: 0,
      ineligible: 0,
      errors: []
    });
    setFinalResults(null);
    setUploadState('idle');
    resetFileUpload();
    setInvoiceNumber('');
    setInvoiceDate('');
  };

  const downloadErrorReport = () => {
    if (progressStats.errors.length === 0) return;

    const csvContent = [
      [t('courierInvoice.rowNumber'), t('courierInvoice.airwaybillNumber'), t('courierInvoice.error')],
      ...progressStats.errors.map(err => [
        err.rowNumber || '',
        err.airwaybillNumber || '',
        err.error || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-errors-${invoiceNumber}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const selectedCourier = businessCouriers.find(c => c.id.toString() === selectedCourierId);

  return (
    <div className={styles.createInvoicePage}>
      {/* Header */}
      <PageHeader
        title={t('courierInvoice.uploadInvoice')}
        description={t('courierInvoice.uploadAndProcessInvoices')}
        icon={FileText}
        actions={
          hasPermission('view_all_courier_invoices') && (
            <Link to="/courier-invoices" className={styles.backButton}>
              <ArrowLeft size={18} />
              {t('courierInvoice.backToInvoices')}
            </Link>
          )
        }
      />

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Courier Selection */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>
              <Truck size={18} />
              {t('courierInvoice.courierAccountSelection')}
            </h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.formGroup}>
              <label>{t('courierInvoice.courierAccountLabel')}</label>
              <Dropdown
                value={selectedCourierId || null}
                onChange={(value) => setSelectedCourierId(value ? value.toString() : '')}
                options={businessCouriers.map(courier => ({
                  value: courier.id.toString(),
                  label: `${courier.accountName} (${courier.CourierCompany?.name})`
                }))}
                placeholder={t('courierInvoice.selectCourierAccount')}
                icon={<Truck size={18} />}
                disabled={uploadState === 'uploading' || uploadState === 'completed'}
              />
            </div>

            {eligibleOrdersInfo && (
              <div className={styles.eligibleOrdersInfo}>
                <div className={styles.infoCard}>
                  <Package size={20} />
                  <div>
                    <span className={styles.infoLabel}>{t('courierInvoice.eligibleOrders')}</span>
                    <span className={styles.infoValue}>{eligibleOrdersInfo.count}</span>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  <DollarSign size={20} />
                  <div>
                    <span className={styles.infoLabel}>{t('courierInvoice.totalValue')}</span>
                    <span className={styles.infoValue}>{formatCurrency(eligibleOrdersInfo.totalValue)}</span>
                  </div>
                </div>
              </div>
            )}

            {columnMapping && (
              <div className={styles.mappingInfo}>
                <div className={styles.mappingHeader}>
                  <CheckCircle size={16} className={styles.successIcon} />
                  <span>{t('courierInvoice.columnMappingLoaded')}: {columnMapping.mappingName}</span>
                </div>
                {columnMapping.notes && (
                  <p className={styles.mappingNotes}>{columnMapping.notes}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Invoice Details Form */}
        {selectedCourierId && columnMapping && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>
                <FileText size={18} />
                {t('courierInvoice.invoiceDetails')}
              </h2>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>{t('courierInvoice.invoiceNumberLabel')}</label>
                  <input
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder={t('courierInvoice.enterInvoiceNumberPlaceholder')}
                    className={styles.input}
                    disabled={uploadState === 'uploading' || uploadState === 'completed'}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>{t('courierInvoice.invoiceDateLabel')}</label>
                  <DatePicker
                    value={invoiceDate}
                    onChange={setInvoiceDate}
                    maxDate={getTodayDate()}
                    disabled={uploadState === 'uploading' || uploadState === 'completed'}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File Upload Section */}
        {selectedCourierId && columnMapping && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>
                <Upload size={18} />
                {t('courierInvoice.uploadInvoiceFile')}
              </h2>
            </div>
            <div className={styles.sectionContent}>
              {!selectedFile ? (
                <div className={styles.uploadArea}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className={styles.fileInput}
                    disabled={uploadState === 'uploading' || uploadState === 'completed'}
                  />
                  <FileSpreadsheet size={48} className={styles.uploadIcon} />
                  <h3>{t('courierInvoice.dropFileHere')}</h3>
                  <p>{t('courierInvoice.supportsFiles')}</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={styles.browseButton}
                    disabled={uploadState === 'uploading' || uploadState === 'completed'}
                  >
                    <Upload size={18} />
                    {t('courierInvoice.selectFile')}
                  </button>
                </div>
              ) : (
                <>
                  <div className={styles.fileInfo}>
                    <FileSpreadsheet size={24} className={styles.fileIcon} />
                    <div className={styles.fileDetails}>
                      <span className={styles.fileName}>{selectedFile.name}</span>
                      <span className={styles.fileSize}>
                        {(selectedFile.size / 1024).toFixed(2)} KB • {t('courierInvoice.ordersCount', { count: totalRows })}
                      </span>
                    </div>
                    {uploadState !== 'uploading' && uploadState !== 'completed' && (
                      <button
                        onClick={resetFileUpload}
                        className={styles.removeFileButton}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  {/* Mapping Warnings */}
                  {mappingWarnings.length > 0 && (
                    <div className={styles.warningsSection}>
                      <div className={styles.warningHeader}>
                        <AlertTriangle size={18} />
                        <span>{t('courierInvoice.columnMappingWarnings', { count: mappingWarnings.length })}</span>
                      </div>
                      {mappingWarnings.map((warning, index) => (
                        <div key={index} className={styles.warningItem}>
                          {warning.message}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* File Preview */}
                  {filePreview.length > 0 && (
                    <div className={styles.previewSection}>
                      <h3>{t('courierInvoice.filePreviewTitle')}</h3>
                      <div className={styles.tableContainer}>
                        <table className={styles.previewTable}>
                          <thead>
                            <tr>
                              {fileHeaders.map((header, index) => (
                                <th key={index}>{header}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filePreview.map((row, rowIndex) => (
                              <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                  <td key={cellIndex}>{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {selectedFile && uploadState === 'ready' && (
          <div className={styles.actionButtons}>
            <button
              onClick={handleStartUpload}
              className={styles.primaryButton}
              disabled={!invoiceNumber || !invoiceDate}
            >
              <Upload size={18} />
              {t('courierInvoice.startProcessing')}
            </button>
          </div>
        )}

        {/* Progress Section */}
        {(uploadState === 'uploading' || uploadState === 'paused') && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>
                <Loader2 size={18} className={uploadState === 'uploading' ? styles.spinning : ''} />
                {t('courierInvoice.processingInvoice')}
              </h2>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.progressInfo}>
                <span>{t('courierInvoice.processingChunkProgress', { current: currentChunk, total: totalChunks })}</span>
                <span>{t('courierInvoice.ordersProcessedProgress', { processed: progressStats.processedCount, total: totalRows })}</span>
              </div>
              
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill}
                  style={{ width: `${(currentChunk / totalChunks) * 100}%` }}
                />
              </div>

              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <CheckCircle className={styles.successIcon} size={20} />
                  <div>
                    <span className={styles.statLabel}>{t('courierInvoice.matched')}</span>
                    <span className={styles.statValue}>{progressStats.matched}</span>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <AlertCircle className={styles.errorIcon} size={20} />
                  <div>
                    <span className={styles.statLabel}>{t('courierInvoice.notFound')}</span>
                    <span className={styles.statValue}>{progressStats.notFound}</span>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <AlertTriangle className={styles.warningIcon} size={20} />
                  <div>
                    <span className={styles.statLabel}>{t('courierInvoice.alreadyInvoiced')}</span>
                    <span className={styles.statValue}>{progressStats.alreadyInvoiced}</span>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <X className={styles.errorIcon} size={20} />
                  <div>
                    <span className={styles.statLabel}>{t('courierInvoice.ineligible')}</span>
                    <span className={styles.statValue}>{progressStats.ineligible}</span>
                  </div>
                </div>
              </div>

              <div className={styles.progressActions}>
                <button
                  onClick={handlePauseResume}
                  className={styles.secondaryButton}
                >
                  {uploadState === 'uploading' ? (
                    <>
                      <Pause size={18} />
                      {t('courierInvoice.pauseUpload')}
                    </>
                  ) : (
                    <>
                      <Play size={18} />
                      {t('courierInvoice.resumeUpload')}
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancelUpload}
                  className={styles.dangerButton}
                >
                  <X size={18} />
                  {t('courierInvoice.cancelUpload')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {uploadState === 'completed' && finalResults && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>
                <CheckCircle size={18} className={styles.successIcon} />
                {t('courierInvoice.processingComplete')}
              </h2>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.resultsGrid}>
                <div className={styles.resultCard}>
                  <Package size={24} />
                  <div>
                    <span className={styles.resultLabel}>{t('courierInvoice.totalOrders')}</span>
                    <span className={styles.resultValue}>
                      {finalResults.summary?.totalOrders || 0}
                    </span>
                  </div>
                </div>
                <div className={styles.resultCard}>
                  <CheckCircle size={24} className={styles.successIcon} />
                  <div>
                    <span className={styles.resultLabel}>{t('courierInvoice.matched')}</span>
                    <span className={styles.resultValue}>
                      {finalResults.summary?.matchedOrders || 0}
                    </span>
                  </div>
                </div>
                <div className={styles.resultCard}>
                  <TrendingUp size={24} className={styles.successIcon} />
                  <div>
                    <span className={styles.resultLabel}>{t('courierInvoice.delivered')}</span>
                    <span className={styles.resultValue}>
                      {finalResults.summary?.deliveredOrders || 0}
                    </span>
                  </div>
                </div>
                <div className={styles.resultCard}>
                  <TrendingDown size={24} className={styles.warningIcon} />
                  <div>
                    <span className={styles.resultLabel}>{t('courierInvoice.returned')}</span>
                    <span className={styles.resultValue}>
                      {finalResults.summary?.returnedOrders || 0}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.financialSummary}>
                <h3>{t('courierInvoice.financialSummary')}</h3>
                <div className={styles.financialGrid}>
                  <div className={styles.financialItem}>
                    <span>{t('courierInvoice.codCollected')}:</span>
                    <strong>{formatCurrency(finalResults.summary?.totalCodCollected || 0)}</strong>
                  </div>
                  <div className={styles.financialItem}>
                    <span>{t('courierInvoice.courierCharges')}:</span>
                    <strong className={styles.errorText}>
                      {formatCurrency(finalResults.summary?.totalCourierCharges || 0)}
                    </strong>
                  </div>
                  <div className={styles.financialItem}>
                    <span>{t('courierInvoice.netAmountPayable')}:</span>
                    <strong className={
                      (finalResults.summary?.totalAmountPayable || 0) >= 0 
                        ? styles.successText 
                        : styles.errorText
                    }>
                      {formatCurrency(finalResults.summary?.totalAmountPayable || 0)}
                    </strong>
                  </div>
                </div>
              </div>

              {progressStats.errors.length > 0 && (
                <div className={styles.errorsSection}>
                  <div className={styles.errorHeader}>
                    <AlertCircle size={18} />
                    <span>{t('courierInvoice.errorsFound', { count: progressStats.errors.length })}</span>
                    <button
                      onClick={downloadErrorReport}
                      className={styles.downloadButton}
                    >
                      <Download size={16} />
                      {t('courierInvoice.downloadErrorReport')}
                    </button>
                  </div>
                </div>
              )}

              <div className={styles.resultActions}>
                <button
                  onClick={() => navigate(`/courier-invoices/${invoiceId}`)}
                  className={styles.primaryButton}
                >
                  <Eye size={18} />
                  {t('courierInvoice.viewInvoiceDetails')}
                </button>
                <button
                  onClick={resetUpload}
                  className={styles.secondaryButton}
                >
                  {t('courierInvoice.uploadAnotherInvoice')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateCourierInvoicePage;